import React from 'react';

const noop = () => undefined;

export type ProgramProgressToolbarPreviewMode = 'track' | 'fixed' | 'project';

export type ProgramProgressToolbarPreviewProps = {
  mode: ProgramProgressToolbarPreviewMode;
};

/**
 * 프로그램 진도표 상단 툴바와 동일한 레이아웃·라벨(미리보기: 전부 비활성).
 */
export const ProgramProgressToolbarPreview: React.FC<ProgramProgressToolbarPreviewProps> = ({ mode }) => {
  const showMergeDuplicate = mode !== 'fixed';

  return (
    <div
      className="cp-program-progress-toolbar"
      role="region"
      aria-label="프로그램 진도표 도구(미리보기, 비활성)"
    >
      <div className="cp-program-progress-toolbar-leading">
        <div className="cp-program-progress-toolbar-leading-set" role="group" aria-label="탐색·오늘·내일">
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-program-browse-nav"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-program-browse-nav-icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 14 14" focusable="false">
                <path
                  d="M7 2.2 2.4 6.8l1.6 1.6 1.9-1.9V12h2.2V6.5L10 8.4l1.6-1.6L7 2.2z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span className="cp-btn-label-desktop">선택셀 연간진도표 탐색</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              탐색
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-lesson-quick"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">오늘 학습내용 보기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              오늘 학습내용
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-lesson-quick"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">내일 학습내용 보기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              내일 학습내용
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-lesson-quick"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">프레젠테이션 열기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              프레젠테이션
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-program-browse-nav"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">프로그램 데이터 정리</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              데이터 정리
            </span>
          </button>
        </div>
      </div>
      <div className="cp-program-progress-toolbar-trailing cp-annual-term-title-inline-actions">
        <div className="cp-program-progress-toolbar-group" role="group" aria-label="행 편집 도구">
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">수업 추가</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              +
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">수업 삭제</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              -
            </span>
          </button>
          {showMergeDuplicate ? (
            <>
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-swap-region"
                disabled
                tabIndex={-1}
                onClick={noop}
                title="미리보기에서는 사용할 수 없습니다."
              >
                <span className="cp-btn-label-desktop">수업 통합</span>
                <span className="cp-btn-label-mobile-icon" aria-hidden>
                  통합
                </span>
              </button>
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-swap-region"
                disabled
                tabIndex={-1}
                onClick={noop}
                title="미리보기에서는 사용할 수 없습니다."
              >
                <span className="cp-btn-label-desktop">수업 증배</span>
                <span className="cp-btn-label-mobile-icon" aria-hidden>
                  증배
                </span>
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">편집내용 올리기</span>
            <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
              <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                <path d="M6 2 2.5 5.5l1.2 1.2L5 5.4V12h2V5.4l1.3 1.3 1.2-1.2L6 2z" fill="currentColor" />
                <path
                  d="M14 12l3.5-3.5-1.2-1.2L15 8.6V2h-2v6.6l-1.3-1.3-1.2 1.2L14 12z"
                  fill="currentColor"
                  opacity="0.45"
                />
              </svg>
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">편집내용 내리기</span>
            <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
              <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                <path d="M6 12l3.5-3.5-1.2-1.2L7 8.6V2H5v6.6L3.7 7.3 2.5 8.5 6 12z" fill="currentColor" />
                <path
                  d="M14 2l-3.5 3.5 1.2 1.2L13 5.4V12h2V5.4l1.3 1.3 1.2-1.2L14 2z"
                  fill="currentColor"
                  opacity="0.45"
                />
              </svg>
            </span>
          </button>
        </div>
        <div className="cp-program-progress-toolbar-group" role="group" aria-label="차시 자동 카운트">
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region cp-btn-period-count"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">차시 자동 카운트</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              차시 카운트
            </span>
          </button>
        </div>
        <div className="cp-program-progress-toolbar-group" role="group" aria-label="클립보드·삭제">
          <button
            type="button"
            className="cp-btn-copy-paste"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">복사</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/copy2.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">잘라내기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/cut.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">붙여넣기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/paste.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste"
            disabled
            tabIndex={-1}
            onClick={noop}
            title="미리보기에서는 사용할 수 없습니다."
          >
            <span className="cp-btn-label-desktop">데이터 삭제</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/del.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
